import React from 'react';

export type BillingCycle = 'WEEKLY' | 'MONTHLY';

export const formatPlanPrice = (amount: number): string =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);

/** The plan we lead with: the everything / premium package. */
export const isPopularPlan = (packageCode = ''): boolean => /premium|everything/i.test(packageCode);

const Tick: React.FC = () => (
  <svg className="h-5 w-5 text-racing-green mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

interface PlanCardProps {
  name: string;
  price: number;
  /** 'weekly', 'monthly' or 'once'. */
  period: string;
  description?: string;
  features?: string[];
  popular?: boolean;
  /** Picked in a multi-select checkout (the paywall). */
  selected?: boolean;
  onCardClick?: () => void;
  /** The card's button (Join Now, Select, Current Plan ...). */
  children: React.ReactNode;
}

/**
 * One membership package, as the pricing page shows it. The pricing page and
 * the tips paywall both use it, so a package looks the same wherever it is sold.
 */
export const PlanCard: React.FC<PlanCardProps> = ({ name, price, period, description, features = [], popular, selected, onCardClick, children }) => (
  <div
    onClick={onCardClick}
    className={`relative bg-white rounded-lg border-2 p-6 flex flex-col ${onCardClick ? 'cursor-pointer' : ''} ${
      selected ? 'border-racing-green bg-racing-green/5 shadow-lg' : popular ? 'border-racing-green shadow-lg md:scale-105' : 'border-gray-200'
    }`}
  >
    {popular && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
        <span className="bg-racing-green text-white px-4 py-1 rounded-full text-sm font-medium whitespace-nowrap">Most Popular</span>
      </div>
    )}

    <div className="text-center mb-6">
      <h3 className="text-2xl font-bold text-deep-navy mb-2">{name}</h3>
      <div className="mb-4">
        <span className="text-4xl font-bold text-deep-navy">{formatPlanPrice(price)}</span>
        <span className="text-gray-600 ml-1">{period === 'once' ? ' once' : `/${period}`}</span>
      </div>
      {description && <p className="text-gray-600">{description}</p>}
    </div>

    {features.length > 0 && (
      <div className="mb-8 space-y-3">
        {features.map((f) => (
          <div key={f} className="flex items-start">
            <Tick />
            <span className="text-sm text-gray-700">{f}</span>
          </div>
        ))}
      </div>
    )}

    <div className="mt-auto">{children}</div>
  </div>
);

/** The pricing page's Weekly / Monthly switch, and what a calendar month buys you. */
export const BillingToggle: React.FC<{ cycle: BillingCycle; onChange: (c: BillingCycle) => void; discount?: boolean }> = ({ cycle, onChange, discount = true }) => (
  <div className="flex flex-col items-center gap-3">
    <div className="bg-gray-100 rounded-lg p-1 flex" role="group" aria-label="Billing">
      {(['WEEKLY', 'MONTHLY'] as const).map((c) => (
        <button
          key={c}
          type="button"
          aria-pressed={cycle === c}
          onClick={() => onChange(c)}
          className={`px-6 py-3 rounded-md text-sm font-medium transition-colors ${
            cycle === c ? 'bg-white text-deep-navy shadow-sm' : 'text-gray-700 hover:text-deep-navy'
          }`}
        >
          {c === 'WEEKLY' ? 'Weekly' : 'Monthly'}
          {c === 'MONTHLY' && discount && (
            <span className="ml-2 bg-race-gold text-deep-navy px-2 py-1 rounded-full text-xs font-medium">Discount</span>
          )}
        </button>
      ))}
    </div>
    {cycle === 'MONTHLY' && (
      <p className="text-sm text-gray-600 text-center">
        Billed each calendar month, not every four weeks: <b className="text-deep-navy">4 extra weeks of tips every year</b>.
      </p>
    )}
  </div>
);
